import EntityFilter from "src/components/common/EntityFilter";
import { AxiosResponse } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
import HorizontalTabs from "src/components/common/HorizontalTabs";
import Snackbar from "src/components/common/Snackbar";
import { memorandumTab, ProjectType } from "src/constants/common";
import { refetchAllProjects } from "src/redux/slices/dashboard/projects";
import { setIsError } from "src/redux/slices/newProjectSetup";

import { Box, Chip } from "@mui/material";
import { Button } from "src/components/common/FormComponents/Buttons";
import { useAppSelector } from "src/redux/store";
import { getTreePlantation } from "src/utils/api/createProjectFlow";
import { dateAndTimeStamp, downloadSheet } from "src/utils/helper";
import {
  RenderButton,
  RenderChip,
  RenderView,
  RenderMOUButton,
} from "src/components/feature/DetailedProjectReport/RenderComponent";
import RenderTable from "src/components/feature/DetailedProjectReport/RenderTable";
import SearchBar from "src/components/common/SearchField";
import { path } from "src/constants/path";

const MOU_STATUS_LABELS: Record<string, string> = {
  DPR_APPROVED: "DPR Approved",
  MOU_DRAFT: "Draft",
  MOU_SUBMIT: "Submitted",
  MOU_DNO_QUERY: "Query from DNO",
  MOU_UNDER_SNO: "Under SNO Review",
  MOU_SNO_QUERY: "Query from SNO",
  MOU_UNDER_ADMIN: "Under Admin Review",
  MOU_ADMIN_QUERY: "Query from Admin",
  MOU_APPROVED: "Approved",
};

const MOU_STATUS_COLORS: Record<string, string> = {
  DPR_APPROVED: "#36B1A0",
  MOU_DRAFT: "#757575",
  MOU_SUBMIT: "#1976d2",
  MOU_DNO_QUERY: "#ed6c02",
  MOU_UNDER_SNO: "#1976d2",
  MOU_SNO_QUERY: "#ed6c02",
  MOU_UNDER_ADMIN: "#1976d2",
  MOU_ADMIN_QUERY: "#ed6c02",
  MOU_APPROVED: "#2e7d32",
};

const RenderMouStatus = (params: any) => {
  const mouStatus = params.row?.mouStatus;
  if (!mouStatus) return <Box>—</Box>;
  return (
    <Chip
      label={MOU_STATUS_LABELS[mouStatus] ?? mouStatus}
      size="small"
      sx={{
        backgroundColor: `${MOU_STATUS_COLORS[mouStatus] ?? "#757575"}20`,
        color: MOU_STATUS_COLORS[mouStatus] ?? "#757575",
        fontWeight: 600,
        fontSize: "12px",
      }}
    />
  );
};

const Mapping: Record<
  "BUTTON" | "CHIP" | "RENDER_VIEW" | "MOU_STATUS" | "UPLOAD_MOU",
  (params: any) => JSX.Element
> = {
  BUTTON: RenderButton,
  CHIP: RenderChip,
  RENDER_VIEW: RenderView,
  MOU_STATUS: RenderMouStatus,
  UPLOAD_MOU: RenderMOUButton,
};

const Memorandum = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("currentTab");

  const ALL_MOU_TAB =
    "MOU_SUBMIT,MOU_DNO_QUERY,MOU_UNDER_SNO,MOU_SNO_QUERY,MOU_UNDER_ADMIN,MOU_ADMIN_QUERY,MOU_APPROVED";

  const [activeTab, setActiveTab] = useState(tab ?? ALL_MOU_TAB);

  const user = useAppSelector((state) => state.root.user);

  const tableColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "id",
      headerName: "Id",
      width: 200,
      sortable: false,
      renderCell: "RENDER_VIEW",
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 100,
      sortable: false,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 200,
      sortable: false,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "state",
      headerName: "State Name",
      width: 175,
      sortable: true,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "district",
      headerName: "DISTRICT",
      width: 175,
      sortable: true,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "division",
      headerName: "DIVISION",
      width: 175,
      sortable: true,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "totalArea",
      headerName: "Selected Area(Ha)",
      width: 200,
      sortable: false,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "finalArea",
      headerName: "Final Area(Ha)",
      width: 170,
      sortable: false,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["ORGANIZATION"],
      tab: [],
    },
    {
      field: "created_at",
      headerName: "Project Selection Date",
      width: 250,
      sortable: false,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "updated_at",
      headerName: "Updated at",
      width: 250,
      sortable: true,
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "mouStatus",
      headerName: "MOU STATUS",
      width: 250,
      sortable: false,
      renderCell: "MOU_STATUS",
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "mou",
      headerName: "View/Upload MOU",
      width: 200,
      sortable: false,
      renderCell: "UPLOAD_MOU",
      type: [ProjectType.TREE_PLANTATION],
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
  ];

  const isAllTab = (tabValue: string) => tabValue === ALL_MOU_TAB;

  const [landQueryParams, setLandQueryParams] = useState<any>({
    offset: 0,
    limit: 50,
    download: false,
    sortBy: "updated_at:DESC",
    mouStatus: activeTab,
    searchText: "",
    newSelection: 1,
    ...(isAllTab(activeTab) ? { dprStatus: "DPR_APPROVED" } : {}),
  });

  const [plantationBlockData, setPlantationBlockData] = useState<any>({
    count: 0,
    rows: [],
  });

  const { isError, msg } = useAppSelector(
    (state) => state.root.selectNewProjectSetup.isError
  );

  const { isFetching: isPlantationFetching } = useQuery(
    ["mouProjects", landQueryParams],
    () =>
      getTreePlantation({
        ...landQueryParams,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        const rows = res.data.rows.map((row) => ({
          sno: row?.serialNo,
          id: row.id,
          registrationNo: row.registrationNo,
          entityName: row.user?.firstName ?? "-",
          state: row.state ?? "-",
          district: row?.district ?? "-",
          division: row?.projectDetail?.landSpecification?.division,
          totalArea: `${row.totalArea}`,
          finalArea: row?.netPlantationArea ?? "-",
          mouStatus: row?.mou?.status || "DPR_APPROVED",
          rawDprStatus: "DPR_APPROVED",
          created_at: dateAndTimeStamp(row?.created_at, "IST"),
          updated_at: dateAndTimeStamp(row?.updated_at, "IST"),
        }));

        if (landQueryParams.download) {
          downloadSheet(rows);
          setLandQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          setPlantationBlockData({
            count: res.data.count,
            rows,
          });
        }
      },
      refetchOnMount: true,
    }
  );

  const columns = useMemo(
    () =>
      tableColumns
        .map((item) => ({
          ...item,
          renderCell: Mapping[item.renderCell],
        }))
        .filter(
          (item) =>
            item.type.includes(ProjectType.TREE_PLANTATION) &&
            item.userType.includes(user.data.userType)
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab]
  );

  const handleEntityPagination = (page: any) => {
    setLandQueryParams({
      ...landQueryParams,
      offset: landQueryParams.limit * page,
    });
  };

  const handleEntitySort = (sortByField) => {
    setLandQueryParams({
      ...landQueryParams,
      sortBy: `${sortByField[0]?.field}:${sortByField[0]?.sort.toUpperCase()}`,
    });
  };

  useEffect(() => {
    dispatch(refetchAllProjects(true));
  }, [landQueryParams, dispatch]);

  const exportToExcel = () => {
    setLandQueryParams({
      ...landQueryParams,
      download: true,
    });
  };

  return (
    <DashboardWrapper
      title="Memorandum of Understanding"
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={false}
    >
      <>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              padding: "10px 1%",
              background: "white",
            }}
          >
            <SearchBar
              getText={(e) => {
                setLandQueryParams((a) => {
                  return { ...landQueryParams, searchText: `${e}`, offset: 0 };
                });
              }}
              customStyles={{
                wrapper: {
                  flex: 1,
                  minWidth: "240px",
                },
              }}
            />
            <EntityFilter
              source="landOwnership"
              filters={{
                mouStatus: landQueryParams.mouStatus,
                dprStatus: landQueryParams.dprStatus,
                newSelection: landQueryParams.newSelection,
                searchText: landQueryParams.searchText,
              }}
              value={landQueryParams.entityIds?.split(",") ?? []}
              onChange={(ids) => {
                setLandQueryParams((prev) => ({
                  ...prev,
                  entityIds: ids.length ? ids.join(",") : undefined,
                  offset: 0,
                }));
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: "13%",
              padding: "10px 1%",
              background: "white",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <HorizontalTabs
              tabHeaders={memorandumTab}
              tabIndex={memorandumTab.findIndex(
                (item) => item.value === activeTab
              )}
              onChange={(e: any) => {
                dispatch(refetchAllProjects(true));
                setActiveTab(e);
                navigate(`${path.memorandum}${e ? `?currentTab=${e}` : ""}`);
                const { dprStatus, ...rest } = landQueryParams;
                setLandQueryParams({
                  ...rest,
                  mouStatus: e,
                  offset: 0,
                  ...(isAllTab(e) ? { dprStatus: "DPR_APPROVED" } : {}),
                });
              }}
              components={[<></>, <></>]}
            />
            <Button onClick={exportToExcel} text={"Export"} />
          </Box>
          <RenderTable
            rows={plantationBlockData.rows}
            columns={columns}
            hideFooterPagination={false}
            handleSortModelChange={handleEntitySort}
            isLoading={isPlantationFetching}
            rowCount={plantationBlockData?.count / landQueryParams.limit}
            onPageChange={(page) => handleEntityPagination(page)}
            pageNumber={landQueryParams.offset / landQueryParams.limit + 1}
          />
        </Box>
      </>
      <Snackbar
        message={msg ? msg : "Something Went Wrong"}
        open={isError}
        severity="error"
        duration={2000}
        resetSnackbar={() =>
          dispatch(
            setIsError({
              isError: false,
              msg: "",
            })
          )
        }
      />
    </DashboardWrapper>
  );
};

export default Memorandum;
